import { useEffect, useRef, useState } from "react"
import { SwipeActionRef } from "antd-mobile"
import TrashIcon from "components/Icons/TrashIcon"
import PlusIcon from "components/Icons/PlusIcon"
import SmallFoodImage from "components/foodDetails/share/SmallFoodImage"
import { formatDisplayNumber, getDisplayCalories } from "utils"
import { formatUnit } from "utils/formater"
import { RecentIngredientType } from "model/food/types/recentIngredient.types"
import { FoodImageBox } from "components/foodItem/MealListItem/styled"
import { AddFoodButton } from "components/foodItem/FoodListItem/styled"
import { useAddedFoodAnimation } from "features/food/hooks/animation"
import useDisplayFoodBottomLine from "features/food/hooks/useDisplayFoodBottomLine"
import {
  StyledSwipeAction,
  IngredientItemContent,
  IngredientLeft,
  IngredientInfoBox,
  IngredientName,
  IngredientBottomInfo,
  IngredientUnitAndPortionBox,
  IngredientNutrient,
  IngredientBrand,
} from "./styled"

const AddedButton = () => {
  const style = useAddedFoodAnimation()

  return (
    <AddFoodButton style={{ cursor: "default" }}>
      <svg
        style={{ transform: `scale(${style.scale})`, opacity: style.opacity }}
        xmlns="http://www.w3.org/2000/svg"
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
      >
        <rect width="32" height="32" rx="16" fill="#0FBDD4" />
        <path
          d="M24 10L13 21L8 16"
          stroke="white"
          strokeWidth="3.42857"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </AddFoodButton>
  )
}

interface RecentIngredientItemProps {
  ingredient: RecentIngredientType
  swipedId: string | null
  setSwipedId: (id: string | null) => void
  onAdd: (ingredient: RecentIngredientType) => void
  onSelect: (ingredient: RecentIngredientType) => void
  onDelete: (id: string) => void
}

const RecentIngredientItem = ({
  ingredient,
  swipedId,
  setSwipedId,
  onAdd,
  onSelect,
  onDelete,
}: RecentIngredientItemProps) => {
  const ref = useRef<SwipeActionRef>(null)
  const [showAddedIcon, setShowAddedIcon] = useState(false)

  const brand = ingredient.brand
  const {
    containerRef,
    unitRef,
    brandRef,
    brandMaxWidth,
    unitMaxWidth,
  } = useDisplayFoodBottomLine(brand)

  useEffect(() => {
    if (swipedId && swipedId !== ingredient.id) {
      ref.current?.close()
    }
  }, [swipedId, ingredient.id])

  const handleAdd = () => {
    onAdd(ingredient)
    setShowAddedIcon(true)
    setTimeout(() => {
      setShowAddedIcon(false)
    }, 1000)
  }

  // Ingredients are always EDAMAM
  const isEdamam = true

  const ingredientContent = (
    <IngredientItemContent>
      <IngredientLeft onClick={() => onSelect(ingredient)}>
        <FoodImageBox>
          <SmallFoodImage src={ingredient.imageUrl} />
        </FoodImageBox>

        <IngredientInfoBox>
          <IngredientName>{ingredient.name}</IngredientName>

          <IngredientBottomInfo ref={containerRef}>
            <IngredientUnitAndPortionBox>
              <IngredientNutrient ref={unitRef} style={{ maxWidth: unitMaxWidth }}>
                {getDisplayCalories(ingredient.calorie)} cal,{" "}
                {formatDisplayNumber(ingredient.portion, 2)}{" "}
                {formatUnit(ingredient.unit, isEdamam)}
              </IngredientNutrient>
            </IngredientUnitAndPortionBox>

            {brand && (
              <IngredientBrand ref={brandRef} style={{ maxWidth: brandMaxWidth }}>
                <p>{brand}</p>
              </IngredientBrand>
            )}
          </IngredientBottomInfo>
        </IngredientInfoBox>
      </IngredientLeft>

      {showAddedIcon ? (
        <AddedButton />
      ) : (
        <AddFoodButton onClick={handleAdd}>
          <PlusIcon />
        </AddFoodButton>
      )}
    </IngredientItemContent>
  )

  return (
    <StyledSwipeAction
      closeOnTouchOutside={false}
      ref={ref}
      onActionsReveal={() => {
        setSwipedId(ingredient.id)
      }}
      closeOnAction={false}
      rightActions={[
        {
          key: "delete",
          text: (
            <TrashIcon
              style={{
                width: 22,
                height: 22,
                marginLeft: 2,
              }}
            />
          ),
          color: "danger",
          onClick: () => {
            onDelete(ingredient.id)
          },
        },
      ]}
    >
      {ingredientContent}
    </StyledSwipeAction>
  )
}

export default RecentIngredientItem
